import { useState, useEffect, useCallback } from 'react';
import type { ImageDto } from '@/components/shared/types';
import { getChargeImages, deleteChargeImage } from '../api/chargeImages';

interface UseChargeImagesResult {
  images: ImageDto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  deleteImage: (imageId: number) => Promise<void>;
}

/**
 * Hook to manage charge images
 * - Fetches images for a specific job charge
 * - Provides delete functionality
 * - Handles loading and error states
 */
export function useChargeImages(jobId: number, chargeId: number): UseChargeImagesResult {
  const [images, setImages] = useState<ImageDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getChargeImages(jobId, chargeId);
      setImages(data);
    } catch (err) {
      setError('Failed to load images');
      console.error('Error fetching charge images:', err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, chargeId]);

  const deleteImage = async (imageId: number) => {
    try {
      await deleteChargeImage(jobId, chargeId, imageId);
      // Remove from local state
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error('Error deleting image:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return {
    images,
    isLoading,
    error,
    refetch: fetchImages,
    deleteImage,
  };
}
